import type { StoreDetailsDto } from '@api/__generated__/accounts/accounts.schemas';
import { useGetStorefrontApiV1StoresStoreId } from '@api/__generated__/accounts/stores/stores';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useAuth } from '@hooks/useAuth';
import { delegationTokenConfig } from '@api/auth/config';
import { getAnonId } from '@utils/anonymousId';
import { getUtmParams } from '@utils/utmParams';

export type MenuIcon = {
  order: number;
  key: string;
  element: ReactElement;
  showBadgeCount?: boolean;
};

interface AppContextValue {
  headerContent: ReactNode;
  setHeaderContent: (content: ReactNode) => void;
  menuIcons?: MenuIcon[];
  setMenuIcons: (icons: MenuIcon[]) => void;
  addMenuIcon: (icon: MenuIcon) => void;
  removeMenuIcon: (key: string) => void;
  selectedProductCount: number;
  setSelectedProductCount: (count: number) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  storeDetails: StoreDetailsDto | null;
  userData: { email: string; firstName: string; lastName: string } | null;
  setUserData: (data: { email: string; firstName: string; lastName: string }) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children, slug }: { children: ReactNode; slug?: string }) {
  const [headerContent, setHeaderContent] = useState<ReactNode>(null);
  const [menuIcons, setMenuIcons] = useState<MenuIcon[]>([]);
  const [selectedProductCount, setSelectedProductCount] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [userData, setUserData] = useState<{ email: string; firstName: string; lastName: string } | null>(null);

  const { isAuthenticated } = useAuth();
  const ldClient = useLDClient();
  // Read fresh on every render (not memoized) so the identify effect below can pick up a URL
  // change even when it happens on a render this component didn't otherwise need to re-run for.
  const currentSearch = typeof window !== 'undefined' ? window.location.search : '';

  const { data } = useGetStorefrontApiV1StoresStoreId({ storeId: Number(slug) }, { query: { enabled: !!slug, staleTime: Infinity } });
  const storeDetails: StoreDetailsDto | null = useMemo(() => data ?? null, [data]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${delegationTokenConfig.baseUrl}/me`, {
          credentials: 'include',
        });
        const data = await response.json();
        setUserData({ email: data.email, firstName: data.firstName, lastName: data.lastName });
      } catch {
        // ignore
      }
    };
    fetchUserInfo();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: currentSearch is a trigger, read fresh via getUtmParams() above rather than used directly here
  useEffect(() => {
    if (!ldClient) return;
    const utm = getUtmParams();

    if (isAuthenticated && userData) {
      ldClient.identify({
        kind: 'user',
        key: userData.email,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        ...utm,
      });
    } else if (!isAuthenticated) {
      // Always re-identify anonymous user to clear any stale UTM context from a previous session
      ldClient.identify({
        kind: 'user',
        key: getAnonId(),
        anonymous: true,
        ...utm,
      });
    }
    // currentSearch is included so navigating to a URL carrying new UTM params (e.g. the signup ->
    // builder redirect forwarding utm_variant) re-identifies with them, rather than only reacting
    // to auth/user changes that may fire before the URL itself has updated.
  }, [isAuthenticated, userData, ldClient, currentSearch]);

  const set = useCallback((content: ReactNode) => {
    setHeaderContent(content);
  }, []);

  const addMenuIcon = useCallback((icon: MenuIcon) => {
    setMenuIcons((prev) => {
      const existingMenuIcon = prev.find((x) => x.key === icon.key);
      if (existingMenuIcon) {
        return prev;
      }
      return [...prev, icon];
    });
  }, []);

  const removeMenuIcon = useCallback((key: string) => {
    setMenuIcons((prev) => (prev ? prev.filter((x) => x.key !== key) : []));
  }, []);

  const value = useMemo(
    () => ({
      headerContent,
      setHeaderContent: set,
      menuIcons,
      setMenuIcons,
      addMenuIcon,
      removeMenuIcon,
      selectedProductCount,
      setSelectedProductCount,
      currentStep,
      setCurrentStep,
      storeDetails,
      userData,
      setUserData,
    }),
    [headerContent, set, menuIcons, addMenuIcon, removeMenuIcon, selectedProductCount, currentStep, storeDetails, userData],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
}
